// Iter 88o — Parked pool right-pane view (extracted from JobBoard.jsx).
//
// Shows every candidate parked across every posting with checkbox
// multi-select + bulk action bar. Kept as a plain component: parent
// (`JobBoard.jsx`) owns the selection Set and the bulk action handlers.

import { Archive, ArrowRight, Loader2, RefreshCw, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmt } from "./utils";

export function ParkedPool({
  resumes, loading, search, onSearch, onTransfer, onRestore, onDelete,
  selected, onToggle, onToggleAll, onClearSelection,
  onBulkTransfer, onBulkRestore, onBulkDelete, bulkBusy,
}) {
  const selectedCount = selected?.size || 0;
  const allSelected   = resumes.length > 0 && selectedCount === resumes.length;
  const someSelected  = selectedCount > 0 && !allSelected;
  return (
    <>
      <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-200 bg-amber-50/70">
        <Archive className="h-5 w-5 text-amber-700" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-amber-900">Parked candidates</h2>
          <p className="text-xs text-amber-800/80">
            Candidates paused across every posting. AI scores are cleared on park
            (they&rsquo;re role-specific). Transfer any of them into an open role.
          </p>
        </div>
      </div>
      <div className="px-6 py-2 border-b border-slate-100 bg-slate-50">
        <input value={search} onChange={(e) => onSearch(e.target.value)}
               placeholder="Search parked pool (name, email, filename, body)…"
               data-testid="parked-search"
               className="h-8 w-96 rounded border border-slate-200 bg-white px-3 text-xs" />
      </div>
      {selectedCount > 0 && (
        <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2"
             data-testid="parked-bulk-bar">
          <span className="text-xs font-medium text-amber-900" data-testid="parked-bulk-count">
            {selectedCount} selected
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <Button size="sm" variant="outline" disabled={bulkBusy}
                    className="h-7 px-2 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    onClick={onBulkTransfer}
                    data-testid="parked-bulk-transfer">
              <ArrowRight className="h-3.5 w-3.5" /> Transfer
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy}
                    className="h-7 px-2 gap-1 text-slate-700"
                    onClick={onBulkRestore}
                    data-testid="parked-bulk-restore"
                    title="Restore each to its original posting as 'new'">
              <RefreshCw className="h-3.5 w-3.5" /> Restore
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy}
                    className="h-7 px-2 gap-1 text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={onBulkDelete}
                    data-testid="parked-bulk-delete">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </Button>
            <Button size="sm" variant="ghost" disabled={bulkBusy}
                    className="h-7 px-2 gap-1 text-slate-500"
                    onClick={onClearSelection}
                    data-testid="parked-bulk-clear">
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          </div>
        </div>
      )}
      <div className="overflow-auto max-h-[calc(100vh-260px)]">
        <table className="w-full text-sm" data-testid="parked-table">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <th className="w-8 pl-6 pr-2 py-2 text-left">
                <input type="checkbox"
                       checked={allSelected}
                       ref={(el) => { if (el) el.indeterminate = someSelected; }}
                       onChange={(e) => onToggleAll(e.target.checked)}
                       disabled={resumes.length === 0}
                       aria-label="Select all parked candidates"
                       data-testid="parked-select-all"
                       className="h-3.5 w-3.5 rounded border-slate-300" />
              </th>
              <th className="px-3 py-2 text-left font-medium">Candidate</th>
              <th className="px-3 py-2 text-left font-medium">Originally applied for</th>
              <th className="px-3 py-2 text-left font-medium">UC Rank</th>
              <th className="px-3 py-2 text-left font-medium">Parked</th>
              <th className="px-6 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-6 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>}
            {!loading && resumes.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500" data-testid="parked-empty">
                Nothing parked yet. Candidates get here when a reviewer clicks &ldquo;Park&rdquo;.
              </td></tr>
            )}
            {!loading && resumes.map((r) => {
              const isChecked = selected?.has(r.resume_id);
              return (
              <tr key={r.resume_id}
                  className={`border-b border-slate-50 hover:bg-amber-50/30 ${isChecked ? "bg-amber-50/50" : ""}`}
                  data-testid={`parked-row-${r.resume_id}`}>
                <td className="pl-6 pr-2 py-3 align-top">
                  <input type="checkbox"
                         checked={!!isChecked}
                         onChange={() => onToggle(r.resume_id)}
                         aria-label={`Select ${r.candidate_name || r.filename}`}
                         data-testid={`parked-select-${r.resume_id}`}
                         className="h-3.5 w-3.5 rounded border-slate-300" />
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="font-medium text-slate-900">{r.candidate_name || "— unknown —"}</div>
                  <div className="text-xs text-slate-500">
                    {r.candidate_email || <span className="text-slate-300">no email</span>}
                    {r.candidate_phone && <> · {r.candidate_phone}</>}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{r.filename}</div>
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="text-xs text-slate-700">{r.parked_from_posting_title || r.posting_title || "—"}</div>
                  <div className="text-[10px] text-slate-400">{r.department_key?.replace(/_/g," ") || ""}</div>
                </td>
                <td className="px-3 py-3 align-top">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                    {r.uc_rank ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-3 align-top text-xs text-slate-500">{fmt(r.parked_at)}</td>
                <td className="px-6 py-3 align-top text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="outline"
                            className="h-7 px-2 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                            onClick={() => onTransfer(r)}
                            data-testid={`parked-transfer-${r.resume_id}`}>
                      <ArrowRight className="h-3.5 w-3.5" /> Transfer
                    </Button>
                    <Button size="sm" variant="outline"
                            className="h-7 px-2 text-slate-700"
                            onClick={() => onRestore(r)}
                            data-testid={`parked-restore-${r.resume_id}`}
                            title="Restore to original posting as 'new'">
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline"
                            className="h-7 px-2 text-rose-600 border-rose-200 hover:bg-rose-50"
                            onClick={() => onDelete(r)}
                            data-testid={`parked-delete-${r.resume_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
